// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import {
    convertDateOnlyStringToDate,
    convertDateToDateOnlyString,
} from "@insite/client-framework/Common/Utilities/DateUtilities";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Tag, { TagPresentationProps } from "@insite/mobius/Tag";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import SearchFieldBidNumber from "@steiner/App/Components/OpenBids/SearchFieldBidNumber";
import SearchFieldDateRange from "@steiner/App/Components/OpenBids/SearchFieldDateRange";
import SearchFieldPoOrJobName from "@steiner/App/Components/OpenBids/SearchFieldPoOrJobName";
import SearchFieldReleaseNumber from "@steiner/App/Components/OpenBids/SearchFieldReleaseNumber";
import SearchFieldSelectShipTo from "@steiner/App/Components/OpenBids/SearchFieldSelectShipTo";
import clearOpenBidsSearchParams from "@steiner/App/Handlers/OpenBids/ClearOpenBidsSearchParams";
import updateOpenBidsSearchFields from "@steiner/App/Handlers/OpenBids/UpdateOpenBidsSearchFields";
import { OpenBidsApiParameter } from "@steiner/App/Services/OpenBidsService";
import { CustomAppState } from "@steiner/App/Store/Reducers";
import { DatePickerState } from "@steiner/MobiusOverrides/DatePicker";
import { GridContainerProps } from "@steiner/MobiusOverrides/GridContainer/GridContainer";
import { XNGAGE_ELEMENTS } from "@steiner/XDL/constants";
import debounce from "lodash/debounce";
import React, { useState } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";

const enum fields {
    showTagsOnly = "showTagsOnly",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showTagsOnly]: boolean;
    };
}

const mapStateToProps = (state: CustomAppState) => ({
    pagination: state.xOpenBids.pagination,
    filters: state.xOpenBids.searchParams,
});

const mapDispatchToProps = {
    updateOpenBidsSearchFields,
    clearOpenBidsSearchParams,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface OpenBidsFiltersStyles {
    filtersGridContainer?: GridContainerProps;
    appliedFiltersContainer?: InjectableCss;
    appliedFilterTag?: TagPresentationProps;
    filterButtonsGridItem?: GridItemProps;
    clearFiltersButton?: ButtonPresentationProps;
}
export const openBidsFiltersStyles: OpenBidsFiltersStyles = {};

const styles = openBidsFiltersStyles;

const OpenBidsFilters = ({ filters, updateOpenBidsSearchFields, clearOpenBidsSearchParams, fields }: Props) => {
    const defaultSearchParameters = {
        customerSequence: "-1",
        poNumber: "",
        orderNumber: "",
        releaseNumber: "",
        fromDate: "",
        toDate: "",
    };
    const [searchParams, setSearchParams] = useState<OpenBidsApiParameter>(defaultSearchParameters);

    React.useEffect(() => {
        if (filters) {
            setSearchParams(filters); // Sync local state with Redux state
        }
    }, [filters]);
    const debouncedUpdateOpenbidsFilters = React.useMemo(
        () => debounce(updateOpenBidsSearchFields, 300),
        [updateOpenBidsSearchFields],
    );
    const onChangeFilter = (filterKey: string, value: any) => {
        if (searchParams[filterKey as keyof typeof searchParams] === value) {
            return;
        }

        setSearchParams(prev => {
            const updatedParams = { ...prev, [filterKey]: value };
            debouncedUpdateOpenbidsFilters({
                key: filterKey,
                value,
            });
            return updatedParams; // Return the updated state
        });
    };
    const onDeleteFilter = (filterKey: string, filterValue?: string) => {
        setSearchParams(prev => ({
            ...prev,
            [filterKey]: filterValue ?? undefined, // Reset only the specific key
        }));
        debouncedUpdateOpenbidsFilters({
            key: filterKey,
            value: filterValue ?? undefined,
        });
    };
    const clearSearchHandler = () => {
        setSearchParams(defaultSearchParameters);
        clearOpenBidsSearchParams({});
    };

    const SearchTags = ({
        filters,
        onDelete,
    }: {
        filters: Record<string, string | undefined>;
        onDelete: (filterKey: string, filterValue?: string) => void;
    }) => {
        const translateLabel = (key: string, value: string) => {
            const labels: Record<string, string> = {
                customerSequence: translate("Ship To"),
                poNumber: translate("PO #"),
                orderNumber: translate("Bid #"),
                releaseNumber: translate("Release #"),
                fromDate: translate("From"),
                toDate: translate("To"),
            };
            return `${labels[key] || key}: ${value}`;
        };

        return (
            <StyledWrapper {...styles.appliedFiltersContainer}>
                {Object.entries(filters).map(([key, value]) =>
                    value ? (
                        <Tag
                            key={key}
                            {...styles.appliedFilterTag}
                            data-test-selector={`filterTag_${key}`}
                            onDelete={() => onDelete(key, key === "customerSequence" ? "-1" : undefined)}
                        >
                            {translateLabel(key, value)}
                        </Tag>
                    ) : null,
                )}
            </StyledWrapper>
        );
    };

    if (fields.showTagsOnly) {
        return (
            <SearchTags
                filters={{
                    customerSequence: !filters?.customerSequence
                        ? translate("Billing Address")
                        : filters?.customerSequence !== "-1"
                        ? filters?.customerSequence
                        : undefined,
                    poNumber: filters?.poNumber,
                    orderNumber: filters?.orderNumber,
                    releaseNumber: filters?.releaseNumber,
                    fromDate: filters?.fromDate,
                    toDate: filters?.toDate,
                }}
                onDelete={onDeleteFilter}
            />
        );
    }
    return (
        <GridContainer {...styles.filtersGridContainer}>
            <SearchFieldSelectShipTo
                selectProps={{
                    value: searchParams.customerSequence ?? defaultSearchParameters.customerSequence,
                    label: translate("Ship-To Address"),
                    onChange: e => {
                        onChangeFilter("customerSequence", e.target.value);
                    },
                }}
            />
            <SearchFieldPoOrJobName
                textFieldProps={{
                    value: searchParams.poNumber ?? defaultSearchParameters.poNumber,
                    label: translate("PO or Job Name"),
                    type: "poNumber",
                    maxLength: 15,
                    placeholder: translate("Enter PO or Job Name"),
                    onChange: e => {
                        onChangeFilter("poNumber", e.target.value);
                    },
                }}
            />
            <SearchFieldDateRange
                fromDate={convertDateOnlyStringToDate(searchParams.fromDate)}
                toDate={convertDateOnlyStringToDate(searchParams.toDate)}
                fromDateChangeHandler={({ selectedDay }: Pick<DatePickerState, "selectedDay">) => {
                    onChangeFilter("fromDate", convertDateToDateOnlyString(selectedDay));
                }}
                toDateChangeHandler={({ selectedDay }: Pick<DatePickerState, "selectedDay">) => {
                    onChangeFilter("toDate", convertDateToDateOnlyString(selectedDay));
                }}
            />
            <SearchFieldReleaseNumber
                textFieldProps={{
                    value: searchParams.releaseNumber ?? defaultSearchParameters.releaseNumber,
                    label: translate("Release #"),
                    placeholder: translate("Enter release #"),
                    onChange: e => {
                        onChangeFilter("releaseNumber", e.target.value);
                    },
                }}
            />
            <SearchFieldBidNumber
                textFieldProps={{
                    value: searchParams.orderNumber ?? defaultSearchParameters.orderNumber,
                    label: translate("Bid #"),
                    placeholder: translate("Enter bid #"),
                    onChange: e => {
                        onChangeFilter("orderNumber", e.target.value);
                    },
                }}
            />
            <GridItem {...styles.filterButtonsGridItem}>
                <Button {...styles.clearFiltersButton} variant="secondary" onClick={clearSearchHandler}>
                    {translate("Clear Filters")}
                </Button>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(OpenBidsFilters),
    definition: {
        group: XNGAGE_ELEMENTS,
        displayName: "Open Bids Filters",
        allowedContexts: ["OpenBidsPage"],
        fieldDefinitions: [
            {
                name: fields.showTagsOnly,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
    },
};

export default widgetModule;
